import { useState } from 'react';
import type { ChatUiTripVoucher } from '@voll-intelligence/genui-contract';
import { CaretDownIcon } from '@phosphor-icons/react';
import { useGenUiI18n } from '../ui/i18n.js';
import { fmtDateTime } from './trip_format.js';
import { MODAL_UI, modalLabel, voucherStatusLabel, VOUCHER_STATUS_UI } from './trip_maps.js';
import { KvGrid, ModalIcon } from './trip_primitives.js';
import { VoucherDetail } from './voucher_detail.js';
import './trip.css';

/** Badge de status do voucher (pill tonal). */
function VoucherStatusBadge({ status }: { readonly status: string | null }) {
  const { t } = useGenUiI18n();
  const meta = status == null ? undefined : VOUCHER_STATUS_UI[status];
  if (!meta) return null;
  return <span className={`tbadge tbadge--${meta.variant}`}>{voucherStatusLabel(status, t)}</span>;
}

/** Título do voucher: rota da jornada, senão fornecedor, senão rótulo do modal. */
function voucherTitle(voucher: ChatUiTripVoucher, t: ReturnType<typeof useGenUiI18n>['t']): string {
  const j = voucher.journey;
  if (j?.origin && j.destination) return `${j.origin} → ${j.destination}`;
  if (voucher.supplier) return voucher.supplier;
  return modalLabel(voucher.modal, t) ?? t('voucher.meta.heading');
}

/** Linha `modal · voucherCode` (mono). Cada parte é um nó de texto próprio para
 *  permitir a busca isolada do código; o separador é decorativo. */
function VoucherCode({
  modal,
  code,
}: {
  readonly modal: string | null;
  readonly code: string | null;
}) {
  return (
    <span className="vacc__code">
      {modal && <span>{modal}</span>}
      {modal && code && <span aria-hidden="true"> · </span>}
      {code && <span>{code}</span>}
    </span>
  );
}

/** Corpo do acordeão: bloco meta (Fornecedor/Status/Reserva/Emitido em) + o
 *  detalhe por modal. */
function VoucherBody({
  voucher,
  statusLabel,
}: {
  readonly voucher: ChatUiTripVoucher;
  readonly statusLabel?: string;
}) {
  const { t, locale } = useGenUiI18n();
  return (
    <div className="vacc__body">
      <div className="vacc__block">
        <span className="vacc__blabel">{t('voucher.meta.heading')}</span>
        <KvGrid
          rows={[
            { k: t('voucher.meta.supplier'), v: voucher.supplier ?? null },
            { k: t('voucher.meta.status'), v: statusLabel ?? null },
            {
              k: t('voucher.meta.reservation'),
              v: voucher.reservationNumber ?? null,
              empty: t('common.toDefine'),
            },
            {
              k: t('voucher.meta.issuedAt'),
              v: fmtDateTime(null, voucher.emittedAt, locale),
              empty: t('voucher.meta.notIssued'),
            },
          ]}
        />
      </div>
      <VoucherDetail
        details={voucher.details ?? null}
        reservationNumber={voucher.reservationNumber ?? null}
      />
    </div>
  );
}

/**
 * Acordeão de um voucher dentro do drawer de detalhe. Cabeçalho clicável (tile
 * do modal, `modal · voucherCode`, título, badge de status, caret que gira);
 * corpo com bloco meta (Fornecedor / Status / Reserva / Emitido em) + o detalhe
 * por modal. `defaultOpen` abre o primeiro voucher.
 */
export function VoucherAccordion({
  voucher,
  defaultOpen,
}: {
  readonly voucher: ChatUiTripVoucher;
  readonly defaultOpen?: boolean;
}) {
  const { t } = useGenUiI18n();
  const [open, setOpen] = useState(defaultOpen ?? false);
  const modalMeta = voucher.modal == null ? undefined : MODAL_UI[voucher.modal];
  const statusLabel = voucherStatusLabel(voucher.status, t);

  return (
    <div className={`vacc${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="vacc__head"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className={`vacc__ico vacc__ico--${voucher.modal ?? 'FLIGHT'}`}>
          {modalMeta ? <ModalIcon icon={modalMeta.icon} size={16} /> : null}
        </span>
        <span className="vacc__main">
          <VoucherCode modal={voucher.modal} code={voucher.voucherCode} />
          <span className="vacc__title">{voucherTitle(voucher, t)}</span>
        </span>
        <VoucherStatusBadge status={voucher.status} />
        <span className="vacc__chev">
          <CaretDownIcon size={15} aria-hidden />
        </span>
      </button>

      {open && <VoucherBody voucher={voucher} statusLabel={statusLabel} />}
    </div>
  );
}
